"use client";

import React from "react";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/tabs/tabs";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/carousel/carousel";
import CardsGrid from "@/components/cards/cardsGrid/cardsGrid";
import CardContentItem from "@/components/cards/cardContent/cardContent";
import { captureEvent } from "@/lib/analytics";
import { Separator } from "@/components/separator/separator";
import type { CardSection as CardSectionType, ContentCard } from "@/types/cms";
import styles from "./CardSection.module.css";

// Pure bucketing helpers moved to pricingBuckets.ts so CardSection can use them
// without statically importing this (embla/Radix-heavy) module. Import the
// types for local use and re-export everything for any existing importers.
import type { PricingTabKey, PricingBuckets } from "./pricingBuckets";
export { getPricingTab, bucketPricingCards } from "./pricingBuckets";
export type { PricingTabKey, PricingBuckets };

interface PricingTabsProps {
  sectionId: string;
  /** Section-level class string (already includes the `.callout` modifier). */
  sectionClassName: string;
  /** Container class string (already includes the column-gap class). */
  containerClassName: string;
  showDivider: boolean;
  dividerClassName: string;
  /** The section header node (already wrapped in `.priceHeader`). */
  header: React.ReactNode;
  gridType?: CardSectionType["type"];
  buckets: PricingBuckets;
  getCardVariant: (card: ContentCard) => "default" | "testimonial" | "price" | "lesson";
}

/**
 * Tabbed pricing layout — a Subscription / One-time switcher above the
 * price cards. Renders the active tab's cards using the same
 * mobile-carousel + desktop-grid layout the flat price section uses, so
 * spacing/visuals are unchanged.
 */
const PricingTabs = ({
  sectionId,
  sectionClassName,
  containerClassName,
  showDivider,
  dividerClassName,
  header,
  gridType,
  buckets,
  getCardVariant,
}: PricingTabsProps) => {
  const [activeTab, setActiveTab] = React.useState<PricingTabKey>("one-time");

  // Mobile carousel + desktop grid for one bucket. Rendered inside the active
  // TabsContent panel only (see below) so the embla carousel always mounts in a
  // visible container.
  const renderCards = (cardsList: ContentCard[]) => (
    <>
      <div
        className={[styles.carouselWrap, styles.onlySm, styles.carouselFullSm].join(
          " ",
        )}
      >
        <Carousel>
          <CarouselContent
            className={[styles.carouselContentSm, styles.carouselBadgeRoomSm].join(" ")}
          >
            {cardsList.map((card, idx) => (
              <CarouselItem
                key={card.id ?? `${idx}-${card.header || "card"}`}
                className={styles.carouselItemSm}
              >
                <CardContentItem card={card} variant={getCardVariant(card)} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
      <div className={styles.hideSm}>
        <CardsGrid
          type={gridType}
          cards={cardsList}
          getCardVariant={getCardVariant}
        />
      </div>
    </>
  );

  return (
    <section id={sectionId} className={sectionClassName} data-ph-pricing>
      <div className={containerClassName}>
        {showDivider && (
          <div className={dividerClassName}>
            <Separator />
          </div>
        )}
        {header}
        <Tabs
          size="lg"
          value={activeTab}
          onValueChange={(v) => {
            captureEvent("pricing_tab_switched", { from_tab: activeTab, to_tab: v });
            setActiveTab(v as PricingTabKey);
          }}
        >
          <div className={styles.tabsRow}>
            <TabsList>
              <TabsTrigger value="one-time">One-Time Purchase</TabsTrigger>
              <TabsTrigger value="subscription">Monthly</TabsTrigger>
            </TabsList>
          </div>
          {/* Real tab panels so each trigger's `aria-controls` resolves to an
              existing element (axe `aria-valid-attr-value`). `forceMount` keeps
              BOTH panels in the DOM — including the inactive one — so the
              inactive trigger's target exists too. The inactive panel is hidden
              via CSS (`data-state="inactive"`) and only the active panel renders
              its cards, so the mobile embla carousel always mounts in a visible
              container. Mobile = carousel, desktop = grid (flat-price layout). */}
          <TabsContent value="subscription" forceMount>
            {activeTab === "subscription" && renderCards(buckets.subscription)}
          </TabsContent>
          <TabsContent value="one-time" forceMount>
            {activeTab === "one-time" && renderCards(buckets.oneTime)}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default PricingTabs;
